import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, ShoppingCart, MessageCircle, Download,
  CheckCircle, X, Fuel, Zap, Settings, Users, Calendar, Gauge,
  Waves, Ruler, LayoutGrid, Sun, Battery, Home, Package, Cpu
} from 'lucide-react';

const DetailPage = ({ selectedVehicle, categoryInfo, formatPrice, addToCart }) => {
  const navigate = useNavigate();
  const [activeImg, setActiveImg] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const [addedToCart, setAddedToCart] = useState(false);

  useEffect(() => {
    if (!selectedVehicle) navigate('/stock');
    window.scrollTo(0, 0);
    setActiveImg(0);
  }, [selectedVehicle, navigate]);

  const images = selectedVehicle?.images || [];
  const total = images.length;
  const cat = selectedVehicle?.category;
  const sub = selectedVehicle?.subcategory;

  const goNext = useCallback(() => setActiveImg(i => (i + 1) % Math.max(total, 1)), [total]);
  const goPrev = useCallback(() => setActiveImg(i => (i - 1 + Math.max(total, 1)) % Math.max(total, 1)), [total]);

  useEffect(() => {
    if (!total) return;
    const handler = (e) => {
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'Escape') setLightbox(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [activeImg, total, goNext, goPrev]);

  if (!selectedVehicle) return null;

  const catBadge = {
    Voiture:  'bg-blue-50 text-blue-600 border-blue-100',
    Moto:     'bg-orange-50 text-orange-600 border-orange-100',
    Bateau:   'bg-cyan-50 text-cyan-600 border-cyan-100',
    Bungalow: 'bg-amber-50 text-amber-700 border-amber-100',
  };

  const subLabel = {
    habitat: 'Habitat modulaire',
    kit:     'Kit énergie',
  };

  // ── PDF ─────────────────────────────────────────────────────────
  const handleOrderSummary = () => {
    const win = window.open('', '_blank');
    const isBungalow = cat === 'Bungalow';
    const isKit = sub === 'kit';

    const specsRows = isBungalow
      ? isKit
        ? `
          ${selectedVehicle.technologie ? `<div class="item"><div class="label">Technologie</div><div class="val">${selectedVehicle.technologie}</div></div>` : ''}
          ${selectedVehicle.capacite    ? `<div class="item"><div class="label">Capacité</div><div class="val">${selectedVehicle.capacite}</div></div>` : ''}
          ${selectedVehicle.tension     ? `<div class="item"><div class="label">Tension</div><div class="val">${selectedVehicle.tension}</div></div>` : ''}
          ${selectedVehicle.garantie    ? `<div class="item"><div class="label">Garantie</div><div class="val">${selectedVehicle.garantie}</div></div>` : ''}
        `
        : `
          ${selectedVehicle.surface      ? `<div class="item"><div class="label">Surface</div><div class="val">${selectedVehicle.surface}</div></div>` : ''}
          ${selectedVehicle.pieces       ? `<div class="item"><div class="label">Pièces</div><div class="val">${selectedVehicle.pieces}</div></div>` : ''}
          ${selectedVehicle.dimensions   ? `<div class="item col2"><div class="label">Dimensions</div><div class="val">${selectedVehicle.dimensions}</div></div>` : ''}
          ${selectedVehicle.construction ? `<div class="item col2"><div class="label">Construction</div><div class="val">${selectedVehicle.construction}</div></div>` : ''}
        `
      : `
        ${selectedVehicle.year         ? `<div class="item"><div class="label">Année</div><div class="val">${selectedVehicle.year}</div></div>` : ''}
        ${selectedVehicle.power        ? `<div class="item"><div class="label">Puissance</div><div class="val">${selectedVehicle.power}</div></div>` : ''}
        ${selectedVehicle.fuel         ? `<div class="item"><div class="label">Carburant</div><div class="val">${selectedVehicle.fuel}</div></div>` : ''}
        ${selectedVehicle.transmission ? `<div class="item"><div class="label">Transmission</div><div class="val">${selectedVehicle.transmission}</div></div>` : ''}
        ${selectedVehicle.seats        ? `<div class="item"><div class="label">Places</div><div class="val">${selectedVehicle.seats}</div></div>` : ''}
        ${selectedVehicle.length       ? `<div class="item"><div class="label">Longueur</div><div class="val">${selectedVehicle.length}</div></div>` : ''}
      `;

    win.document.write(`
      <html><head><title>TECHNIC IMPORT – ${selectedVehicle.name}</title>
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;700;900&display=swap');
        body { font-family:'Inter',sans-serif; padding:48px; color:#111; margin:0; background:#fff; }
        .header { display:flex; justify-content:space-between; align-items:flex-start; border-bottom:3px solid #dc2626; padding-bottom:20px; margin-bottom:28px; }
        .logo { font-size:22px; font-weight:900; font-style:italic; text-transform:uppercase; }
        .logo span { color:#dc2626; }
        .date { font-size:11px; color:#9ca3af; text-transform:uppercase; letter-spacing:0.1em; margin-top:4px; }
        h2 { font-size:26px; font-weight:900; margin:0 0 8px; text-transform:uppercase; font-style:italic; }
        .brand { font-size:11px; color:#dc2626; font-weight:700; text-transform:uppercase; letter-spacing:0.15em; margin-bottom:4px; }
        .price-box { background:#111827; color:white; border-radius:12px; padding:16px 24px; margin:20px 0; }
        .price-label { font-size:10px; color:#9ca3af; text-transform:uppercase; letter-spacing:0.15em; margin-bottom:6px; }
        .price-value { font-size:32px; color:#ef4444; font-weight:900; font-style:italic; }
        .grid { display:grid; grid-template-columns:1fr 1fr; gap:10px; margin-top:24px; }
        .item { background:#f9fafb; padding:12px 16px; border-radius:10px; border:1px solid #f3f4f6; }
        .col2 { grid-column: span 2; }
        .label { font-size:9px; color:#9ca3af; text-transform:uppercase; letter-spacing:0.15em; font-weight:700; margin-bottom:4px; }
        .val { font-size:13px; font-weight:700; color:#111; }
        .footer { margin-top:48px; font-size:10px; color:#d1d5db; border-top:1px solid #f3f4f6; padding-top:16px; text-align:center; letter-spacing:0.1em; text-transform:uppercase; }
      </style></head><body>
      <div class="header">
        <div>
          <div class="logo">Technic<span>Import</span></div>
          <div class="date">Document informatif — ${new Date().toLocaleDateString('fr-FR')}</div>
        </div>
        <div style="text-align:right;font-size:11px;color:#6b7280">Importation directe<br>Outre-Mer</div>
      </div>
      <div class="brand">${selectedVehicle.brand} · ${cat || ''} ${sub ? '· ' + (subLabel[sub] || sub) : ''}</div>
      <h2>${selectedVehicle.name}</h2>
      <div class="price-box">
        <div class="price-label">Tarif import clé en main</div>
        <div class="price-value">${selectedVehicle.price ? formatPrice(selectedVehicle.price) : 'Sur demande'}</div>
      </div>
      <div class="grid">${specsRows}</div>
      <div class="footer">Document non contractuel · Technic Import Outre-Mer · contact@technic-import.com</div>
      </body></html>
    `);
    win.document.close();
    win.print();
  };

  const handleWhatsApp = () => {
    const msg = encodeURIComponent(
      `Bonjour Technic Import,\n\nJe souhaite obtenir des informations sur :\n\n` +
      `*${selectedVehicle.brand} ${selectedVehicle.name}*\n` +
      `${cat === 'Bungalow' && sub === 'kit' ? '' : `Année : ${selectedVehicle.year || 'N/A'}\n`}` +
      `Prix import : ${selectedVehicle.price ? formatPrice(selectedVehicle.price) : 'Sur demande'}\n\n` +
      `Merci de me recontacter.`
    );
    window.open(`https://wa.me/33601905164?text=${msg}`, '_blank');
  };

  const handleAddToCart = () => {
    addToCart(selectedVehicle);
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2500);
  };

  // ── Spec badge générique ────────────────────────────────────────
  const Spec = ({ label, value, icon: Icon, wide = false }) => {
    if (!value && value !== 0) return null;
    return (
      <div className={`flex items-start gap-3 p-3.5 bg-gray-50 rounded-xl border border-gray-100 hover:bg-gray-100 transition-colors ${wide ? 'col-span-2' : ''}`}>
        {Icon && (
          <div className="w-7 h-7 bg-white rounded-lg border border-gray-200 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Icon size={13} className="text-red-600" />
          </div>
        )}
        <div className="min-w-0">
          <p className="text-[9px] text-gray-400 font-black uppercase tracking-widest mb-0.5">{label}</p>
          <p className="text-xs font-bold text-gray-900 leading-snug">{value}</p>
        </div>
      </div>
    );
  };

  // ── Section bungalow HABITAT ────────────────────────────────────
  const BungalowHabitatSpecs = () => (
    <>
      {/* Chiffres clés en grand */}
      {(selectedVehicle.surface || selectedVehicle.pieces) && (
        <div className="col-span-2 grid grid-cols-2 gap-3 mb-1">
          {selectedVehicle.surface && (
            <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4 text-center">
              <p className="text-[9px] text-amber-600 font-black uppercase tracking-widest mb-1">Surface</p>
              <p className="text-2xl font-black text-amber-700 italic">{selectedVehicle.surface}</p>
            </div>
          )}
          {selectedVehicle.pieces && (
            <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4 text-center">
              <p className="text-[9px] text-amber-600 font-black uppercase tracking-widest mb-1">Pièces</p>
              <p className="text-2xl font-black text-amber-700 italic">{selectedVehicle.pieces}</p>
            </div>
          )}
        </div>
      )}
      <Spec label="Dimensions" value={selectedVehicle.dimensions} icon={Ruler} wide />
      <Spec label="Construction" value={selectedVehicle.construction} icon={Settings} wide />
      <Spec label="Année" value={selectedVehicle.year} icon={Calendar} />
    </>
  );

  // ── Section bungalow KIT ÉNERGIE ────────────────────────────────
  const BungalowKitSpecs = () => (
    <>
      {/* Capacité en vedette */}
      {selectedVehicle.capacite && (
        <div className="col-span-2 bg-gradient-to-br from-yellow-50 to-amber-50 border border-amber-100 rounded-2xl p-5 text-center mb-1">
          <p className="text-[9px] text-amber-600 font-black uppercase tracking-widest mb-2">Capacité</p>
          <p className="text-3xl font-black text-amber-700 italic">{selectedVehicle.capacite}</p>
        </div>
      )}
      <Spec label="Technologie" value={selectedVehicle.technologie} icon={Cpu} wide />
      <Spec label="Tension" value={selectedVehicle.tension} icon={Zap} />
      <Spec label="Garantie" value={selectedVehicle.garantie} icon={CheckCircle} />
      <Spec label="Année" value={selectedVehicle.year} icon={Calendar} />
    </>
  );

  // ── Specs par catégorie ─────────────────────────────────────────
  const renderSpecs = () => {
    if (cat === 'Bungalow') {
      return sub === 'kit' ? <BungalowKitSpecs /> : <BungalowHabitatSpecs />;
    }
    if (cat === 'Bateau') return (
      <>
        <Spec label="Année"       value={selectedVehicle.year}        icon={Calendar} />
        <Spec label="Puissance"   value={selectedVehicle.power}       icon={Zap} />
        <Spec label="Carburant"   value={selectedVehicle.fuel}        icon={Fuel} />
        <Spec label="Type moteur" value={selectedVehicle.type_moteur} icon={Settings} wide />
        <Spec label="Longueur"    value={selectedVehicle.length}      icon={Ruler} />
        <Spec label="Places"      value={selectedVehicle.seats}       icon={Users} />
        <Spec label="Réservoir"   value={selectedVehicle.reservoir}   icon={Waves} />
        <Spec label="Tirant d'eau" value={selectedVehicle.tirant_eau} icon={Waves} />
        <Spec label="Charge utile" value={selectedVehicle.charge_utile} icon={Gauge} />
      </>
    );
    if (cat === 'Moto') return (
      <>
        <Spec label="Année"        value={selectedVehicle.year}         icon={Calendar} />
        <Spec label="Puissance"    value={selectedVehicle.power}        icon={Zap} />
        <Spec label="Carburant"    value={selectedVehicle.fuel}         icon={Fuel} />
        <Spec label="Type moteur"  value={selectedVehicle.type_moteur}  icon={Settings} wide />
        <Spec label="Transmission" value={selectedVehicle.transmission} icon={Settings} />
        <Spec label="Places"       value={selectedVehicle.seats}        icon={Users} />
        <Spec label="Réservoir"    value={selectedVehicle.reservoir}    icon={Waves} />
        <Spec label="Consommation" value={selectedVehicle.consommation} icon={Gauge} />
      </>
    );
    return (
      <>
        <Spec label="Année"        value={selectedVehicle.year}            icon={Calendar} />
        <Spec label="Puissance"    value={selectedVehicle.power}           icon={Zap} />
        <Spec label="Carburant"    value={selectedVehicle.fuel}            icon={Fuel} />
        <Spec label="Carrosserie"  value={selectedVehicle.type_carrosserie} icon={LayoutGrid} />
        <Spec label="Type moteur"  value={selectedVehicle.type_moteur}     icon={Settings} wide />
        <Spec label="Transmission" value={selectedVehicle.transmission}    icon={Settings} />
        <Spec label="Places"       value={selectedVehicle.seats}           icon={Users} />
        <Spec label="Réservoir"    value={selectedVehicle.reservoir}       icon={Waves} />
        <Spec label="Consommation" value={selectedVehicle.consommation}    icon={Gauge} wide />
        <Spec label="Remorquage"   value={selectedVehicle.charge_remorquage} icon={Gauge} />
        <Spec label="Charge utile" value={selectedVehicle.charge_utile}   icon={Gauge} />
      </>
    );
  };

  // ── Couleur accent bungalow ─────────────────────────────────────
  const isBungalow = cat === 'Bungalow';
  const isKit = sub === 'kit';
  const accentPrice = isBungalow ? 'text-amber-500' : 'text-red-500';
  const accentCTA   = isBungalow ? 'bg-amber-500 hover:bg-amber-600 shadow-amber-500/20' : 'bg-red-600 hover:bg-red-700 shadow-red-600/20';

  return (
    <div className="min-h-screen bg-white pb-28 lg:pb-12">

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-[300] bg-black/95 flex items-center justify-center" onClick={() => setLightbox(false)}>
          <button onClick={() => setLightbox(false)} className="absolute top-4 right-4 text-white/60 hover:text-white z-10 p-2 hover:bg-white/10 rounded-full transition-all">
            <X size={24} />
          </button>
          <button onClick={e => { e.stopPropagation(); goPrev(); }} className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/25 text-white p-3 rounded-full transition-all z-10">
            <ChevronLeft size={24} />
          </button>
          <img src={images[activeImg]} alt={selectedVehicle.name} className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg" onClick={e => e.stopPropagation()} />
          <button onClick={e => { e.stopPropagation(); goNext(); }} className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/25 text-white p-3 rounded-full transition-all z-10">
            <ChevronRight size={24} />
          </button>
          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 text-white/40 text-xs font-mono">{activeImg + 1} / {total}</div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 py-6 md:py-10">

        <button onClick={() => navigate(-1)} className="flex items-center gap-2 mb-6 text-gray-400 hover:text-red-600 transition-colors font-semibold uppercase text-xs tracking-widest group">
          <ChevronLeft size={16} className="group-hover:-translate-x-1 transition-transform" /> Retour
        </button>

        <div className="flex flex-col lg:flex-row gap-10 lg:gap-14">

          {/* ── IMAGES ── */}
          <div className="w-full lg:w-3/5 space-y-3">
            <div className="relative group rounded-2xl overflow-hidden bg-gray-900 shadow-xl cursor-zoom-in" style={{ aspectRatio: '4/3' }} onClick={() => total > 0 && setLightbox(true)}>
              <img
                src={images[activeImg] || ''}
                alt={selectedVehicle.name}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                onError={e => { e.target.src = 'https://via.placeholder.com/800x600/111827/6b7280?text=Image+Indisponible'; }}
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center pointer-events-none">
                <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-black/70 text-white text-[10px] font-bold uppercase tracking-widest px-4 py-2 rounded-full backdrop-blur-sm">
                  Cliquer pour agrandir
                </span>
              </div>
              {total > 1 && (
                <>
                  <button onClick={e => { e.stopPropagation(); goPrev(); }} className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/80 text-white p-2.5 rounded-full opacity-0 group-hover:opacity-100 transition-all backdrop-blur-sm"><ChevronLeft size={18} /></button>
                  <button onClick={e => { e.stopPropagation(); goNext(); }} className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/80 text-white p-2.5 rounded-full opacity-0 group-hover:opacity-100 transition-all backdrop-blur-sm"><ChevronRight size={18} /></button>
                  <div className="absolute bottom-3 right-3 bg-black/60 text-white text-xs font-mono px-3 py-1 rounded-full backdrop-blur-sm">{activeImg + 1}/{total}</div>
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white/10">
                    <div className="h-full bg-red-500 transition-all duration-300" style={{ width: `${((activeImg + 1) / total) * 100}%` }} />
                  </div>
                </>
              )}
            </div>
            {total > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-1 no-scrollbar">
                {images.map((img, idx) => (
                  <button key={idx} onClick={() => setActiveImg(idx)}
                    className={`relative flex-shrink-0 w-20 h-14 rounded-xl overflow-hidden border-2 transition-all ${activeImg === idx ? 'border-red-600 opacity-100 shadow-md shadow-red-600/20' : 'border-transparent opacity-50 hover:opacity-80'}`}>
                    <img src={img} alt={`vue ${idx + 1}`} className="w-full h-full object-cover" onError={e => { e.target.src = 'https://via.placeholder.com/80x56/f3f4f6/9ca3af?text=?'; }} />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* ── INFOS ── */}
          <div className="w-full lg:w-2/5">
            <div className="lg:sticky lg:top-24 space-y-5">

              {/* En-tête */}
              <div>
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className="text-red-600 font-black uppercase text-xs tracking-[0.25em]">{selectedVehicle.brand}</span>
                  <span className="text-gray-200">·</span>
                  <span className={`text-[10px] font-bold uppercase px-2.5 py-0.5 rounded-full border ${catBadge[cat] || catBadge.Voiture}`}>{cat}</span>
                  {/* Badge subcategory pour bungalow */}
                  {isBungalow && sub && (
                    <span className={`text-[10px] font-bold uppercase px-2.5 py-0.5 rounded-full border ${isKit ? 'bg-yellow-50 text-yellow-700 border-yellow-100' : 'bg-amber-50 text-amber-700 border-amber-100'}`}>
                      {isKit ? 'Kit énergie' : 'Habitat'}
                    </span>
                  )}
                  {selectedVehicle.year && !isKit && (
                    <span className="text-[10px] font-bold text-gray-400 uppercase">{selectedVehicle.year}</span>
                  )}
                </div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-black italic uppercase text-gray-900 leading-tight">
                  {selectedVehicle.name}
                </h1>
              </div>

              {/* Prix */}
              <div className={`rounded-2xl p-5 text-center relative overflow-hidden ${isBungalow ? 'bg-gray-900' : 'bg-gray-900'}`}>
                <div className={`absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent ${isBungalow ? 'via-amber-500' : 'via-red-600'} to-transparent`} />
                <p className="text-gray-400 text-[10px] font-black uppercase tracking-[0.2em] mb-2">
                  {isBungalow && isKit ? 'Prix unitaire import' : 'Tarif Import Clé en Main'}
                </p>
                <div className={`text-3xl md:text-4xl font-black italic ${accentPrice}`}>
                  {selectedVehicle.price ? formatPrice(selectedVehicle.price) : (
                    <span className="text-xl text-gray-400">Prix sur demande</span>
                  )}
                </div>
                {selectedVehicle.price && (
                  <p className="text-gray-500 text-xs mt-2 font-medium">
                    Économies estimées vs prix local : <span className="text-green-400 font-bold">jusqu'à 25%</span>
                  </p>
                )}
              </div>

              {/* Specs */}
              <div>
                <h3 className="text-[10px] font-black uppercase text-gray-400 tracking-widest mb-3 flex items-center gap-2">
                  <span className="h-px flex-1 bg-gray-100" />
                  {isBungalow && isKit ? 'Caractéristiques techniques' : isBungalow ? 'Caractéristiques habitat' : 'Fiche technique'}
                  <span className="h-px flex-1 bg-gray-100" />
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  {renderSpecs()}
                </div>
              </div>

              {/* Points forts */}
              {selectedVehicle.arguments_vente?.length > 0 && (
                <div>
                  <h3 className="text-[10px] font-black uppercase text-gray-400 tracking-widest mb-3 flex items-center gap-2">
                    <span className="h-px flex-1 bg-gray-100" />
                    Points forts
                    <span className="h-px flex-1 bg-gray-100" />
                  </h3>
                  <div className="space-y-2">
                    {selectedVehicle.arguments_vente.map((arg, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-green-50 rounded-xl border border-green-100">
                        <CheckCircle size={14} className="text-green-500 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-gray-700 leading-relaxed">{arg}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Boutons desktop */}
              <div className="hidden lg:flex flex-col gap-2.5 pt-1">
                <button onClick={handleAddToCart}
                  className={`w-full py-4 rounded-xl font-black uppercase text-sm flex items-center justify-center gap-3 transition-all shadow-lg text-white ${addedToCart ? 'bg-green-500 hover:bg-green-600 shadow-green-500/20' : `${accentCTA} hover:-translate-y-px`}`}>
                  {addedToCart ? <><CheckCircle size={18} /> Ajouté au panier !</> : <><ShoppingCart size={18} /> Ajouter au panier</>}
                </button>
                <button onClick={handleWhatsApp}
                  className="w-full bg-[#25D366] hover:bg-[#20BD5A] text-white py-4 rounded-xl font-black uppercase text-sm flex items-center justify-center gap-3 transition-all hover:-translate-y-px">
                  <MessageCircle size={18} /> WhatsApp Direct
                </button>
                <button onClick={handleOrderSummary}
                  className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 py-3.5 rounded-xl font-bold uppercase text-xs tracking-widest flex items-center justify-center gap-2 transition-all">
                  <Download size={14} /> Télécharger fiche PDF
                </button>
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* Sticky CTA mobile */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-100 p-3 flex gap-2 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]">
        <button onClick={handleAddToCart}
          className={`flex-1 py-3.5 rounded-xl font-black uppercase text-sm flex items-center justify-center gap-2 transition-all shadow-lg text-white ${addedToCart ? 'bg-green-500' : accentCTA}`}>
          {addedToCart ? <><CheckCircle size={16} /> Ajouté !</> : <><ShoppingCart size={16} /> Panier</>}
        </button>
        <button onClick={handleWhatsApp}
          className="flex-1 bg-[#25D366] text-white py-3.5 rounded-xl font-black uppercase text-sm flex items-center justify-center gap-2">
          <MessageCircle size={16} /> WhatsApp
        </button>
        <button onClick={handleOrderSummary}
          className="w-12 bg-gray-100 text-gray-600 rounded-xl flex items-center justify-center flex-shrink-0">
          <Download size={18} />
        </button>
      </div>

    </div>
  );
};

export default DetailPage;
